// Pre-made example currency configurations
import { resolveCurrencyName } from '../utils/currencyLookup';

// Re-export all currency lookup utilities for backward compatibility
export { currencyIdToName, currencyNameToId, resolveCurrencyName, resolveCurrencyId } from '../utils/currencyLookup';

export const exampleCurrencies = [
  {
    id: 'community-token',
    name: 'Community Token',
    description: 'Fixed supply token with multiple preallocations',
    type: 'token',
    config: {
      name: 'CommunityToken',
      options: 32,
      proofprotocol: 1,
      isMintable: false,
    },
    nodes: [
      {
        type: 'preallocation',
        data: { identity: 'CommunityToken@', amount: 5000000 },
      },
      {
        type: 'preallocation',
        data: { identity: 'Treasury@', amount: 2000000 },
      },
      {
        type: 'preallocation',
        data: { identity: 'Team@', amount: 1000000 },
      },
    ],
  },
  {
    id: 'loyalty-points',
    name: 'Loyalty Points',
    description: 'Mintable token for rewards program',
    type: 'token',
    config: {
      name: 'StorePoints',
      options: 32,
      proofprotocol: 2,
      isMintable: true,
    },
    nodes: [
      {
        type: 'preallocation',
        data: { identity: 'StorePoints@', amount: 100000 },
      },
    ],
  },
  {
    id: 'vrsc-dai-pool',
    name: 'VRSC-DAI Pool',
    description: 'Two-asset liquidity pool',
    type: 'basket',
    config: {
      name: 'VRSC_DAI_Pool',
      options: 33,
      proofprotocol: 1,
      initialsupply: 100000,
    },
    nodes: [
      {
        type: 'reserveCurrency',
        data: { currency: 'VRSC', weight: 0.5 },
      },
      {
        type: 'reserveCurrency',
        data: { currency: 'DAI.vETH', weight: 0.5 },
      },
      {
        type: 'preallocation',
        data: { identity: 'VRSC_DAI_Pool@', amount: 1000 },
      },
    ],
  },
  {
    id: 'crypto-index',
    name: 'Precious Metals Index',
    description: 'Multi-asset basket with precious metals backing',
    type: 'basket',
    config: {
      name: 'MetalsIndex',
      options: 33,
      proofprotocol: 1,
      initialsupply: 1000000,
    },
    nodes: [
      {
        type: 'reserveCurrency',
        data: { currency: 'VRSC', weight: 0.25 },
      },
      {
        type: 'reserveCurrency',
        data: { currency: 'paxg.vETH', weight: 0.25 },
      },
      {
        type: 'reserveCurrency',
        data: { currency: 'xaut.vETH', weight: 0.25 },
      },
      {
        type: 'reserveCurrency',
        data: { currency: 'DAI.vETH', weight: 0.25 },
      },
    ],
  },
  {
    id: 'usdc-mapped',
    name: 'Mapped myUSDC',
    description: 'ERC-20 USDC bridged to Verus',
    type: 'erc20',
    config: {
      name: 'myUSDC',
      options: 32,
      proofprotocol: 3,
      notarizationprotocol: 3,
    },
    nodes: [
      {
        type: 'erc20',
        data: { address: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', parent: 'vETH' },
      },
    ],
  },
];

// Determine currency type from options
export const getCurrencyType = (options) => {
  if (options & 256) return 'pbaas'; // IS_PBAAS_CHAIN
  if (options & 128) return 'gateway'; // GATEWAY
  if ((options & 1) && (options & 32)) return 'basket'; // FRACTIONAL + TOKEN
  if (options & 32) return 'token'; // TOKEN
  return 'unknown';
};

// Get currency type description
export const getCurrencyTypeInfo = (options, hasNativeCurrencyId) => {
  const type = getCurrencyType(options);
  
  if (hasNativeCurrencyId) {
    return {
      id: 'erc20',
      title: 'Mapped ERC-20',
      subtitle: 'Ethereum Bridge Token',
      color: 'from-orange-500 to-red-600',
    };
  }

  switch (type) {
    case 'pbaas':
      return {
        id: 'pbaas',
        title: 'PBaaS Chain',
        subtitle: 'Independent Blockchain',
        color: 'from-purple-500 to-pink-600',
      };
    case 'gateway':
      return {
        id: 'gateway',
        title: 'Gateway',
        subtitle: 'Cross-chain Bridge',
        color: 'from-cyan-500 to-blue-600',
      };
    case 'basket':
      return {
        id: 'basket',
        title: 'Liquidity Pool',
        subtitle: 'Basket Currency (AMM)',
        color: 'from-emerald-500 to-teal-600',
      };
    case 'token':
      return {
        id: 'token',
        title: 'Simple Token',
        subtitle: 'Fixed or Mintable Supply',
        color: 'from-blue-500 to-indigo-600',
      };
    default:
      return {
        id: 'unknown',
        title: 'Currency',
        subtitle: 'Verus Currency',
        color: 'from-slate-500 to-slate-600',
      };
  }
};

// Parse existing currency to editor format
export const parseCurrencyToEditorFormat = (currencyData, bridgeConverterData = null) => {
  const def = currencyData.currencydefinition;
  const typeInfo = getCurrencyTypeInfo(def.options, !!def.nativecurrencyid);
  
  const result = {
    currencyType: typeInfo,
    mainConfig: {
      name: def.name,
      options: def.options,
      proofprotocol: def.proofprotocol,
      notarizationprotocol: def.notarizationprotocol,
      isMintable: def.proofprotocol === 2,
      initialsupply: def.initialsupply,
      gatewayconvertername: def.gatewayconvertername,
      gatewayconverterid: def.gatewayconverterid,
      idregistrationfees: def.idregistrationfees,
      idreferrallevels: def.idreferrallevels,
      startblock: def.startblock,
      endblock: def.endblock,
    },
    nodes: [],
    bridgeConverterData, // Include bridge converter data if available
  };

  // Add reserve currencies
  if (def.currencies && def.weights) {
    def.currencies.forEach((currId, index) => {
      const currName = resolveCurrencyName(currId);
      result.nodes.push({
        type: 'reserveCurrency',
        data: {
          currency: currName,
          weight: def.weights[index],
        },
      });
    });
  }

  // Add eras for PBaaS chains
  if (def.eras) {
    def.eras.forEach((era) => {
      result.nodes.push({
        type: 'era',
        data: era,
      });
    });
  }

  // Add bootstrap nodes
  if (def.nodes) {
    def.nodes.forEach((node) => {
      result.nodes.push({
        type: 'nodeSettings',
        data: node,
      });
    });
  }

  // Add ERC-20 mapping
  if (def.nativecurrencyid) {
    result.nodes.push({
      type: 'erc20',
      data: {
        address: def.nativecurrencyid.address,
        parent: 'vETH',
      },
    });
  }

  // Add advanced options for PBaaS
  if (typeInfo.id === 'pbaas') {
    result.nodes.push({
      type: 'advancedOptions',
      data: {
        idregistrationfees: def.idregistrationfees || 100,
        idreferrallevels: def.idreferrallevels || 3,
        notarizationreward: def.notarizationreward || 0.0001,
        blocktime: def.blocktime || 60,
        powaveragingwindow: def.powaveragingwindow || 45,
      },
    });
  }

  return result;
};
